"use client";

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { logToSentry } from "../PlatformData/platformDataErrorTracking";
import { getCookieByName } from "../Helpers/cookieConsent";
import { shouldTrack } from "../Helpers/trackingGate";
import { PlatformDataEvents } from "../PlatformData/PlatformDataEvents";
import { eventThrottle } from "../PlatformData/eventThrottle";
import { initializeTracking } from "../PlatformData/initializeTracking";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "../Helpers/getServerConfig";
import { WhitelabelConfig } from "@/whitelabel/helpers/types";
import { cleanObject } from "../Helpers/cleanObject";

// Queue size limits to prevent memory leaks from unbounded growth
const MAX_QUEUE_SIZE = 100;
const MAX_QUEUE_AGE_MS = 60000; // 1 minute - events older than this are dropped

let eventQueue: Array<{
  waitingFor: string;
  trackingObject: any;
  timestamp: number;
}> = [];

const pushToQueue = (eventQueueElement: {
  waitingFor: string;
  trackingObject: any;
}) => {
  const now = Date.now();

  // Remove events older than MAX_QUEUE_AGE_MS to prevent stale events from accumulating
  eventQueue = eventQueue.filter((e) => now - e.timestamp < MAX_QUEUE_AGE_MS);

  // Enforce size limit by removing oldest events if queue is full
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    eventQueue = eventQueue.slice(-(MAX_QUEUE_SIZE - 1));
  }

  eventQueue.push({
    ...eventQueueElement,
    timestamp: now,
  });
};

const pushDelayedEvents = (
  waitingFor: string,
  envConfig: EnvConfig,
  searchParams: URLSearchParams,
) => {
  eventQueue
    .filter((e) => e.waitingFor === waitingFor)
    .forEach((e) => {
      if (typeof window !== "undefined" && window.platformData) {
        e.trackingObject.platformName = "ICARUS";
        window.platformData.push(cleanObject(e.trackingObject));
        sendKibanaLog({
          envConfig: envConfig,
          message: `PLATFORM_DATA_EVENT_PUSHED`,
          component: "platformData",
          category: "platformData",
          searchId: searchParams.get("searchId") ?? "",
          jsonMessage: {
            event: e.trackingObject?.event,
            user: e.trackingObject?.user,
          },
          transactionId: searchParams.get("transactionId") ?? undefined,
        });
      }
    });
  eventQueue = eventQueue.filter((e) => e.waitingFor !== waitingFor);
};

const isEventAvailable = (event: string, sectionId: string | null = null) => {
  if (typeof window === "undefined" || !window.platformData) return false;

  if (sectionId) {
    return window.platformData.some(
      (e) => e.event === event && e.page?.section === sectionId,
    );
  }
  return window.platformData.some((e) => e.event === event);
};

// Wait for GTM to be fully loaded before pushing conversion events.
// On the thank you page, the conversion event fires before gtm.dom/gtm.load,
// which may cause Tealium's referrer exclusion logic to miss the event.
// Sunrise has a similar delay mechanism (500ms + polling).
const waitForGtmReady = (
  maxRetries = 10,
  interval = 200,
): Promise<boolean> => {
  return new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      attempts++;
      if (
        window.platformData?.some(
          (e: { event: string }) => e.event === "gtm.load",
        )
      ) {
        resolve(true);
      } else if (attempts >= maxRetries) {
        resolve(false);
      } else {
        setTimeout(check, interval);
      }
    };
    check();
  });
};

const bd4InteractionAllowed = () => {
  return (
    isEventAvailable(PlatformDataEvents.VIEW_ITEM_LIST) &&
    (isEventAvailable(PlatformDataEvents.TEALIUM_COOKIEGO) ||
      isEventAvailable(PlatformDataEvents.CCM_COOKIEGO))
  );
};

interface PlatformDataState {
  // State
  isInitialized: boolean;
  trackingInitialized: boolean;
  envConfig: EnvConfig | null;

  // Actions
  initialize: (WlConfig: WhitelabelConfig, envConfig: EnvConfig) => void;
  setTrackingInitialized: (initialized: boolean) => void;
  pushEvent: (event: any) => void;
  handleTealiumConsent: (viewMode: any) => void;
}

export const usePlatformDataStore = create<PlatformDataState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    isInitialized: false,
    trackingInitialized: false,
    envConfig: null,

    // Initialize tracking
    initialize: (WlConfig: WhitelabelConfig, envConfig: EnvConfig) => {
      if (get().isInitialized) return;

      set({ isInitialized: true, envConfig });

      if (typeof window !== "undefined" && !window.Cypress) {
        initializeTracking({
          gtmId: WlConfig?.platformDataTracking?.id,
          tealiumUrl: WlConfig?.tealiumUrl,
          currentEnv: envConfig?.currentEnv,
          cookieConsentManager: WlConfig?.cookieConsentManager,
          gaProperty: "UA-77032923-1",
          WlConfig,
        });

        // Set tracking as initialized after initializeTracking is called
        // Wait for 1 second to ensure that the tracking is initialized
        setTimeout(() => {
          get().setTrackingInitialized(true);
        }, 1000);
      }

      // Handle platform cookies
      const platform = getCookieByName("comvel-platform");
      const platformVersion = getCookieByName("comvel-platform-version");

      if (platform) {
        localStorage.setItem("comvel-platform", platform);
      }
      if (platformVersion) {
        localStorage.setItem("comvel-platform-version", platformVersion);
      }
    },

    setTrackingInitialized: (initialized: boolean) => {
      set({ trackingInitialized: initialized });
    },

    // Handle Tealium cookie consent
    handleTealiumConsent: (viewMode: any) => {
      const { trackingInitialized, envConfig } = get();
      if (
        !window?.platformData ||
        !viewMode ||
        !trackingInitialized ||
        !envConfig
      )
        return;

      // Check for Tealium initialization and consent
      const utagMainCookie = getCookieByName("utag_main");
      const isTealiumInitialized = typeof window.utag !== "undefined";
      const hasTealiumConsent = utagMainCookie?.includes("v_id:");

      if (hasTealiumConsent && isTealiumInitialized) {
        // Check if Tealium cookie consent event has already been sent
        const hasAlreadySentTealiumEvent = isEventAvailable(
          PlatformDataEvents.TEALIUM_COOKIEGO,
        );

        if (!hasAlreadySentTealiumEvent) {
          // Send Tealium cookie consent event
          window.platformData.push(
            cleanObject({
              event: PlatformDataEvents.TEALIUM_COOKIEGO,
              platformName: "ICARUS",
            }),
          );
        }

        // Release BD4 interactions since Tealium consent is given
        const searchParams = new URLSearchParams(window.location.search);
        pushDelayedEvents("releaseBd4Interaction", envConfig, searchParams);
      }
    },

    // Push tracking event
    pushEvent: (event: any) => {
      try {
        if (typeof window === "undefined") {
          return;
        }

        const { envConfig } = get();
        if (!envConfig) {
          console.log("[platformDataStore] No envConfig, returning");
          return;
        }

        // Check if event should be throttled
        if (eventThrottle.shouldThrottleEvent(event.event)) {
          return;
        }

        const { shouldTrackPlatformData } = shouldTrack({ envConfig });

        if (!shouldTrackPlatformData) {
          console.info("NEW: LM tracking only for PRODUCTION!", event);
          return;
        }

        if (event.event === PlatformDataEvents.BD4_INTERACTION) {
          if (!bd4InteractionAllowed()) {
            pushToQueue({
              waitingFor: "releaseBd4Interaction",
              trackingObject: event,
            });
            return;
          }
        }

        const isVisitEvent = event.event === PlatformDataEvents.VISIT;
        const isConversionEvent = event.event === PlatformDataEvents.CONVERSION;

        if (
          !isConversionEvent &&
          !isVisitEvent &&
          !isEventAvailable(PlatformDataEvents.VISIT)
        ) {
          pushToQueue({
            waitingFor: PlatformDataEvents.VISIT,
            trackingObject: event,
          });
          return;
        }

        // Conversion events must wait for GTM to be fully loaded so that
        // Tealium extensions (e.g. null_referrer) can process the event correctly.
        if (isConversionEvent) {
          waitForGtmReady().then(() => {
            if (window.platformData) {
              event.platformName = "ICARUS";
              window.platformData.push(cleanObject(event));

              const searchParams = new URLSearchParams(
                window.location.search,
              );
              pushDelayedEvents(
                PlatformDataEvents.VISIT,
                envConfig,
                searchParams,
              );

              sendKibanaLog({
                envConfig: envConfig,
                message: `PLATFORM_DATA_EVENT_PUSHED`,
                component: "platformData",
                category: "platformData",
                searchId: searchParams.get("searchId") ?? "",
                jsonMessage: {
                  event: event?.event,
                  user: event?.user,
                },
                transactionId:
                  searchParams.get("transactionId") ?? undefined,
              });
            }
          });
          return;
        }

        if (window.platformData) {
          event.platformName = "ICARUS";
          window.platformData.push(cleanObject(event));

          const searchParams = new URLSearchParams(window.location.search);
          pushDelayedEvents(PlatformDataEvents.VISIT, envConfig, searchParams);

          sendKibanaLog({
            envConfig: envConfig,
            message: `PLATFORM_DATA_EVENT_PUSHED`,
            component: "platformData",
            category: "platformData",
            searchId: searchParams.get("searchId") ?? "",
            jsonMessage: {
              event: event?.event,
              user: event?.user,
            },
            transactionId: searchParams.get("transactionId") ?? undefined,
          });
        }
      } catch (error) {
        logToSentry(error as Error, [["function", "pushEvent"]]);
      }
    },
  })),
);

// Hook for tracking (equivalent to usePlatformDataTracking)
export const usePlatformDataTracking = () => {
  const pushEvent = usePlatformDataStore((state) => state.pushEvent);

  return {
    pushEvent,
  };
};

// Initialize the store when imported
if (typeof window !== "undefined") {
  // Auto-initialize when the store is created on client side
  // This could be called from a provider or app initialization
}
